import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, TruckIcon, MapPinIcon, ClockIcon, ShieldIcon, AlertTriangleIcon } from 'lucide-react';
export const ConvoyCard = () => {
  const [openSection, setOpenSection] = useState<string | null>(null);
  const toggleSection = (section: string) => {
    if (openSection === section) {
      setOpenSection(null);
    } else {
      setOpenSection(section);
    }
  };
  return <div className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white" data-id="element-1080">
      {/* Card Header */}
      <div className="p-5 border-b border-gray-200 bg-gray-50" data-id="element-1081">
        <div className="flex items-start justify-between" data-id="element-1082">
          <div data-id="element-1083">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-1084">
              COA Development for Resupply
            </h3>
          </div>
          <div className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium" data-id="element-1085">
            Resupply Required
          </div>
        </div>
      </div>
      {/* COA Sections */}
      <div className="divide-y divide-gray-200" data-id="element-1086">
        {/* LOGPAC Section */}
        <div data-id="element-1087">
          <button onClick={() => toggleSection('logpac')} className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-1088">
            <div className="flex items-center" data-id="element-1089">
              <TruckIcon className="h-5 w-5 text-blue-600 mr-3" data-id="element-1090" />
              <div className="text-left" data-id="element-1091">
                <h4 className="font-medium text-gray-900" data-id="element-1092">LOGPAC</h4>
                <p className="text-sm text-gray-600" data-id="element-1093">
                  Requirements - 3 days, 4 vehicles
                </p>
              </div>
            </div>
            {openSection === 'logpac' ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-1094" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-1095" />}
          </button>
          {/* LOGPAC Content */}
          {openSection === 'logpac' && <div className="px-5 py-4 bg-gray-50 border-t border-gray-100" data-id="element-1096">
              <div className="prose max-w-none" data-id="element-1097">
                <h5 className="text-base font-medium mb-3" data-id="element-1098">LOGPAC Analysis</h5>
                <p data-id="element-1099">
                  A Logistics Package (LOGPAC) approach requires coordination of
                  4 vehicles over 3 days to meet the maintenance and resupply
                  requirements of the convoy.
                </p>
                <div className="mt-4 bg-white p-4 rounded-md border border-gray-200" data-id="element-1100">
                  <h6 className="font-medium text-sm mb-2" data-id="element-1101">Requirements</h6>
                  <ul className="list-disc pl-5 text-sm space-y-1" data-id="element-1102">
                    <li data-id="element-1103">2x HEMTT fuelers (2,500 gal capacity each)</li>
                    <li data-id="element-1104">1x Mobile maintenance team with parts and tools</li>
                    <li data-id="element-1105">
                      1x General supply truck with Class I, III(P), and IX
                    </li>
                  </ul>
                </div>
                <div className="mt-4 bg-white p-4 rounded-md border border-gray-200" data-id="element-1106">
                  <h6 className="font-medium text-sm mb-2" data-id="element-1107">Timeline</h6>
                  <div className="space-y-2 text-sm" data-id="element-1108">
                    <div className="flex" data-id="element-1109">
                      <span className="font-medium w-24" data-id="element-1110">Day 1:</span>
                      <span data-id="element-1111">Preparation and staging at Presidio FOB</span>
                    </div>
                    <div className="flex" data-id="element-1112">
                      <span className="font-medium w-24" data-id="element-1113">Day 2:</span>
                      <span data-id="element-1114">
                        Travel to Convoy Waypoint Alpha (Fort Stockton)
                      </span>
                    </div>
                    <div className="flex" data-id="element-1115">
                      <span className="font-medium w-24" data-id="element-1116">Day 3:</span>
                      <span data-id="element-1117">
                        Service convoy, perform maintenance, and return to
                        Presidio
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex gap-4" data-id="element-1118">
                  <div className="flex-1 bg-white p-4 rounded-md border border-gray-200" data-id="element-1119">
                    <h6 className="font-medium text-sm mb-2" data-id="element-1120">Advantages</h6>
                    <ul className="list-disc pl-5 text-sm space-y-1" data-id="element-1121">
                      <li data-id="element-1122">Minimal resource requirements</li>
                      <li data-id="element-1123">Flexible scheduling</li>
                      <li data-id="element-1124">Rapid deployment capability</li>
                      <li data-id="element-1125">No fixed infrastructure needed</li>
                    </ul>
                  </div>
                  <div className="flex-1 bg-white p-4 rounded-md border border-gray-200" data-id="element-1126">
                    <h6 className="font-medium text-sm mb-2" data-id="element-1127">Disadvantages</h6>
                    <ul className="list-disc pl-5 text-sm space-y-1" data-id="element-1128">
                      <li data-id="element-1129">Limited capacity for major repairs</li>
                      <li data-id="element-1130">Requires convoy to halt for service</li>
                      <li data-id="element-1131">Weather dependent</li>
                      <li data-id="element-1132">Limited duration of support</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 bg-blue-50 p-4 rounded-md border border-blue-200" data-id="element-1133">
                  <h6 className="font-medium text-blue-800 mb-2" data-id="element-1134">
                    Recommendation
                  </h6>
                  <p className="text-sm text-blue-800" data-id="element-1135">
                    The LOGPAC option is recommended if the convoy has minimal
                    anticipated maintenance needs and requires flexibility in
                    scheduling. Best suited for convoys with newer vehicles and
                    experienced maintenance personnel.
                  </p>
                  <div className="mt-4 pt-3 border-t border-blue-200 flex justify-between items-center" data-id="element-1136">
                    <div className="text-sm text-blue-700" data-id="element-1137">
                      Confidence: <span className="font-medium" data-id="element-1138">82%</span>
                    </div>
                    <button className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs font-medium" data-id="element-1139">
                      Generate Implementation Plan
                    </button>
                  </div>
                </div>
              </div>
            </div>}
        </div>
        {/* Supply Node Section */}
        <div data-id="element-1140">
          <button onClick={() => toggleSection('supplynode')} className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-1141">
            <div className="flex items-center" data-id="element-1142">
              <MapPinIcon className="h-5 w-5 text-green-600 mr-3" data-id="element-1143" />
              <div className="text-left" data-id="element-1144">
                <h4 className="font-medium text-gray-900" data-id="element-1145">Supply Node</h4>
                <p className="text-sm text-gray-600" data-id="element-1146">
                  Fixed location resupply and maintenance
                </p>
              </div>
            </div>
            {openSection === 'supplynode' ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-1147" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-1148" />}
          </button>
          {/* Supply Node Content */}
          {openSection === 'supplynode' && <div className="px-5 py-4 bg-gray-50 border-t border-gray-100" data-id="element-1149">
              <div className="prose max-w-none" data-id="element-1150">
                <h5 className="text-base font-medium mb-3" data-id="element-1151">
                  Supply Node Analysis
                </h5>
                <p data-id="element-1152">
                  Establishing a supply node provides a fixed location for
                  maintenance and resupply operations. Two potential routes and
                  supply node locations have been identified for this convoy.
                </p>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1153">
                  {/* Option 1 */}
                  <div className="bg-white p-4 rounded-md border border-gray-200" data-id="element-1154">
                    <h6 className="font-medium mb-3" data-id="element-1155">
                      Option 1: Fort Stockton Supply Node
                    </h6>
                    <div className="space-y-3 mb-4" data-id="element-1156">
                      <div className="flex items-center text-sm" data-id="element-1157">
                        <ClockIcon className="h-4 w-4 text-gray-500 mr-2" data-id="element-1158" />
                        <span className="font-medium mr-2" data-id="element-1159">Travel Time:</span>
                        <span data-id="element-1160">4.5 hours from Presidio</span>
                      </div>
                      <div className="flex items-center text-sm" data-id="element-1161">
                        <TruckIcon className="h-4 w-4 text-gray-500 mr-2" data-id="element-1162" />
                        <span className="font-medium mr-2" data-id="element-1163">Distance:</span>
                        <span data-id="element-1164">240 miles from Presidio</span>
                      </div>
                      <div className="flex items-center text-sm" data-id="element-1165">
                        <MapPinIcon className="h-4 w-4 text-gray-500 mr-2" data-id="element-1166" />
                        <span className="font-medium mr-2" data-id="element-1167">Route:</span>
                        <span data-id="element-1168">US-67 E → I-10 E (Northern Route)</span>
                      </div>
                    </div>
                    <div className="mb-3" data-id="element-1169">
                      <h6 className="text-sm font-medium mb-2" data-id="element-1170">Pros</h6>
                      <ul className="list-disc pl-5 text-sm space-y-1" data-id="element-1171">
                        <li data-id="element-1172">Existing military facilities available</li>
                        <li data-id="element-1173">Good road conditions throughout</li>
                        <li data-id="element-1174">Cell coverage along 95% of route</li>
                        <li data-id="element-1175">Multiple fuel options in Fort Stockton</li>
                      </ul>
                    </div>
                    <div className="mb-3" data-id="element-1176">
                      <h6 className="text-sm font-medium mb-2" data-id="element-1177">Cons</h6>
                      <ul className="list-disc pl-5 text-sm space-y-1" data-id="element-1178">
                        <li data-id="element-1179">Higher traffic volume on I-10</li>
                        <li data-id="element-1180">Longer initial leg from Presidio</li>
                        <li data-id="element-1181">
                          Limited repair facilities for specialized equipment
                        </li>
                        <li data-id="element-1182">Higher visibility to civilian population</li>
                      </ul>
                    </div>
                    <div className="flex items-center mt-3 p-2 bg-gray-100 rounded text-sm" data-id="element-1183">
                      <ShieldIcon className="h-4 w-4 text-green-600 mr-2" data-id="element-1184" />
                      <span className="font-medium mr-2" data-id="element-1185">
                        Security Assessment:
                      </span>
                      <span className="text-green-700" data-id="element-1186">Low Risk</span>
                    </div>
                  </div>
                  {/* Option 2 */}
                  <div className="bg-white p-4 rounded-md border border-gray-200" data-id="element-1187">
                    <h6 className="font-medium mb-3" data-id="element-1188">
                      Option 2: Alpine Supply Node
                    </h6>
                    <div className="space-y-3 mb-4" data-id="element-1189">
                      <div className="flex items-center text-sm" data-id="element-1190">
                        <ClockIcon className="h-4 w-4 text-gray-500 mr-2" data-id="element-1191" />
                        <span className="font-medium mr-2" data-id="element-1192">Travel Time:</span>
                        <span data-id="element-1193">2.75 hours from Presidio</span>
                      </div>
                      <div className="flex items-center text-sm" data-id="element-1194">
                        <TruckIcon className="h-4 w-4 text-gray-500 mr-2" data-id="element-1195" />
                        <span className="font-medium mr-2" data-id="element-1196">Distance:</span>
                        <span data-id="element-1197">135 miles from Presidio</span>
                      </div>
                      <div className="flex items-center text-sm" data-id="element-1198">
                        <MapPinIcon className="h-4 w-4 text-gray-500 mr-2" data-id="element-1199" />
                        <span className="font-medium mr-2" data-id="element-1200">Route:</span>
                        <span data-id="element-1201">TX-67 N → US-90 E (Southern Route)</span>
                      </div>
                    </div>
                    <div className="mb-3" data-id="element-1202">
                      <h6 className="text-sm font-medium mb-2" data-id="element-1203">Pros</h6>
                      <ul className="list-disc pl-5 text-sm space-y-1" data-id="element-1204">
                        <li data-id="element-1205">Shorter distance from Presidio</li>
                        <li data-id="element-1206">Less traffic than Fort Stockton option</li>
                        <li data-id="element-1207">Lower visibility operations</li>
                        <li data-id="element-1208">Sul Ross University resources available</li>
                      </ul>
                    </div>
                    <div className="mb-3" data-id="element-1209">
                      <h6 className="text-sm font-medium mb-2" data-id="element-1210">Cons</h6>
                      <ul className="list-disc pl-5 text-sm space-y-1" data-id="element-1211">
                        <li data-id="element-1212">Limited maintenance facilities</li>
                        <li data-id="element-1213">Fewer fuel options</li>
                        <li data-id="element-1214">Cell coverage gaps (75% coverage)</li>
                        <li data-id="element-1215">
                          Challenging road conditions during inclement weather
                        </li>
                      </ul>
                    </div>
                    <div className="flex items-center mt-3 p-2 bg-gray-100 rounded text-sm" data-id="element-1216">
                      <AlertTriangleIcon className="h-4 w-4 text-amber-600 mr-2" data-id="element-1217" />
                      <span className="font-medium mr-2" data-id="element-1218">
                        Security Assessment:
                      </span>
                      <span className="text-amber-700" data-id="element-1219">Medium Risk</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 bg-green-50 p-4 rounded-md border border-green-200" data-id="element-1220">
                  <h6 className="font-medium text-green-800 mb-2" data-id="element-1221">
                    Recommendation
                  </h6>
                  <p className="text-sm text-green-800" data-id="element-1222">
                    Fort Stockton is recommended as the primary supply node due
                    to superior infrastructure and security profile. Alpine
                    should be designated as an alternate supply node if weather
                    or other conditions make the primary route unsafe.
                  </p>
                  <div className="mt-4 pt-3 border-t border-green-200 flex justify-between items-center" data-id="element-1223">
                    <div className="text-sm text-green-700" data-id="element-1224">
                      Confidence: <span className="font-medium" data-id="element-1225">86%</span>
                    </div>
                    <button className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-xs font-medium" data-id="element-1226">
                      Generate Implementation Plan
                    </button>
                  </div>
                </div>
              </div>
            </div>}
        </div>
        {/* Tailgate Section */}
        <div data-id="element-1227">
          <button onClick={() => toggleSection('tailgate')} className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-1228">
            <div className="flex items-center" data-id="element-1229">
              <TruckIcon className="h-5 w-5 text-purple-600 mr-3" data-id="element-1230" />
              <div className="text-left" data-id="element-1231">
                <h4 className="font-medium text-gray-900" data-id="element-1232">Tailgate</h4>
                <p className="text-sm text-gray-600" data-id="element-1233">
                  Field maintenance with limited resources
                </p>
              </div>
            </div>
            {openSection === 'tailgate' ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-1234" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-1235" />}
          </button>
          {/* Tailgate Content */}
          {openSection === 'tailgate' && <div className="px-5 py-4 bg-gray-50 border-t border-gray-100" data-id="element-1236">
              <div className="prose max-w-none" data-id="element-1237">
                <h5 className="text-base font-medium mb-3" data-id="element-1238">
                  Tailgate Maintenance Analysis
                </h5>
                <p data-id="element-1239">
                  Tailgate maintenance provides a minimal footprint option for
                  field maintenance with limited resources. This approach relies
                  on the convoy's organic maintenance capability with targeted
                  external support.
                </p>
                <div className="mt-4 bg-white p-4 rounded-md border border-gray-200" data-id="element-1240">
                  <h6 className="font-medium text-sm mb-2" data-id="element-1241">Requirements</h6>
                  <ul className="list-disc pl-5 text-sm space-y-1" data-id="element-1242">
                    <li data-id="element-1243">1x Mobile maintenance contact team</li>
                    <li data-id="element-1244">Limited parts package focused on high-failure items</li>
                    <li data-id="element-1245">1x Fuel truck (1,200 gal capacity)</li>
                    <li data-id="element-1246">Basic Class I and water resupply</li>
                  </ul>
                </div>
                <div className="mt-4 bg-white p-4 rounded-md border border-gray-200" data-id="element-1247">
                  <h6 className="font-medium text-sm mb-2" data-id="element-1248">Execution Plan</h6>
                  <p className="text-sm mb-2" data-id="element-1249">
                    Maintenance contact team will rendezvous with the convoy at
                    three predetermined locations:
                  </p>
                  <div className="space-y-2 text-sm" data-id="element-1250">
                    <div className="flex" data-id="element-1251">
                      <span className="font-medium w-24" data-id="element-1252">Location 1:</span>
                      <span data-id="element-1253">Marfa, TX (Day 2)</span>
                    </div>
                    <div className="flex" data-id="element-1254">
                      <span className="font-medium w-24" data-id="element-1255">Location 2:</span>
                      <span data-id="element-1256">Marathon, TX (Day 4)</span>
                    </div>
                    <div className="flex" data-id="element-1257">
                      <span className="font-medium w-24" data-id="element-1258">Location 3:</span>
                      <span data-id="element-1259">Sanderson, TX (Day 5)</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex gap-4" data-id="element-1260">
                  <div className="flex-1 bg-white p-4 rounded-md border border-gray-200" data-id="element-1261">
                    <h6 className="font-medium text-sm mb-2" data-id="element-1262">Advantages</h6>
                    <ul className="list-disc pl-5 text-sm space-y-1" data-id="element-1263">
                      <li data-id="element-1264">Minimal resource footprint</li>
                      <li data-id="element-1265">Highly mobile and adaptable</li>
                      <li data-id="element-1266">Maintains convoy momentum</li>
                      <li data-id="element-1267">Low visibility profile</li>
                      <li data-id="element-1268">Reduced security requirements</li>
                    </ul>
                  </div>
                  <div className="flex-1 bg-white p-4 rounded-md border border-gray-200" data-id="element-1269">
                    <h6 className="font-medium text-sm mb-2" data-id="element-1270">Disadvantages</h6>
                    <ul className="list-disc pl-5 text-sm space-y-1" data-id="element-1271">
                      <li data-id="element-1272">Limited repair capability</li>
                      <li data-id="element-1273">Vulnerable to parts shortages</li>
                      <li data-id="element-1274">Highly dependent on skilled maintenance personnel</li>
                      <li data-id="element-1275">No capacity for major component replacement</li>
                      <li data-id="element-1276">Weather sensitive operations</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 bg-purple-50 p-4 rounded-md border border-purple-200" data-id="element-1277">
                  <h6 className="font-medium text-purple-800 mb-2" data-id="element-1278">
                    Recommendation
                  </h6>
                  <p className="text-sm text-purple-800" data-id="element-1279">
                    Tailgate maintenance is recommended only if the convoy
                    consists primarily of high-reliability vehicles with minimal
                    anticipated maintenance requirements. This option should be
                    paired with a contingency plan for recovery of vehicles with
                    major mechanical failures.
                  </p>
                  <div className="mt-4 pt-3 border-t border-purple-200 flex justify-between items-center" data-id="element-1280">
                    <div className="text-sm text-purple-700" data-id="element-1281">
                      Confidence: <span className="font-medium" data-id="element-1282">74%</span>
                    </div>
                    <button className="px-3 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors text-xs font-medium" data-id="element-1283">
                      Generate Implementation Plan
                    </button>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>
      {/* Card Footer */}
      <div className="p-5 border-t border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-1284"></div>
    </div>;
};